import {announce} from '@github-ui/aria-live'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import type {IssueFormRef} from '@github-ui/issue-form/Types'
import {useEntityFieldsBridge} from '@github-ui/optimistic-connection/connectionMutationBridge'
import {useOptimisticData} from '@github-ui/optimistic-connection/useOptimisticData'
import {keepPreviousData, useQuery} from '@github-ui/react-query'
import {useDebounce} from '@github-ui/use-debounce'
import {verifiedFetchJSON} from '@github-ui/verified-fetch'
import {useCallback, useEffect, useMemo, useRef, useState} from 'react'

import type {PotentialDuplicateIssue} from '../PotentialDuplicates'
import {instanceOfIssueFormData, instanceOfIssueTemplateData, type IssueCreatePayload} from '../utils/model'
import {normalizeForDuplicateThreshold} from '../utils/normalize-input'
import type {IssueCreateContext} from '../utils/option-config'
import {useDuplicateDetectionAnalytics} from './use-duplicate-detection-analytics'

export type PotentialDuplicateIssueUpdate = {
  nodeId: string
  title?: string
  state?: PotentialDuplicateIssue['state']
  stateReason?: PotentialDuplicateIssue['stateReason']
}

export type UseDuplicateDetectionOptions = {
  repoOwner: string
  repoName: string
  title: string
  body: string
  template?: IssueCreatePayload
  issueFormRef?: React.RefObject<IssueFormRef | null>
  isDuplicateDetectionEnabled: boolean
  context: IssueCreateContext
}

/** State for duplicate detection feature */
export type DuplicateDetectionState = {
  items: readonly PotentialDuplicateIssue[]
  isError: boolean
  isDuplicateDetectionEnabled: boolean
  sessionResetKey: number
  /** Update a similar issue in the cache */
  updateIssue: (update: PotentialDuplicateIssueUpdate) => void
  /** Track when the user clicks an issue */
  trackIssueClicked: (position: number) => void
  /** Reset state for a new issue creation session */
  resetForNewSession: () => void
  /** Track when the user creates an issue */
  trackIssueCreated: (issueId: string, issueNumber: number, issueTitle: string, issueBody: string) => void
  dismissCurrentResults: () => void
}

const DEFAULT_MIN_TITLE_LENGTH = 20
export const DEFAULT_MIN_BODY_LENGTH = 100
export const DEFAULT_DEBOUNCE_MS = 1200
export const DEFAULT_RECHECK_DELTA_PERCENT = 0.25

/**
 * Checks if the length delta between current and last queried length is significant.
 * @returns true if the delta is >= threshold (default 25%)
 */
export function meetsDeltaForRecheck(
  currentLength: number,
  lastLength: number,
  threshold: number = DEFAULT_RECHECK_DELTA_PERCENT,
): boolean {
  if (lastLength === 0) return true
  return Math.abs(currentLength - lastLength) / lastLength >= threshold
}

/**
 * Duplicate detection lifecycle.
 * - pending: Waiting for title + body threshold to be met;
 * - active: Has queried at least once, may have items or be empty, tracks lastQueriedLength for re-check;
 */
export const DetectionPhase = {
  PENDING: 'pending',
  ACTIVE: 'active',
} as const
type DetectionPhase =
  | {status: typeof DetectionPhase.PENDING}
  | {status: typeof DetectionPhase.ACTIVE; lockedItems: PotentialDuplicateIssue[]; lastQueriedLength: number}

const INITIAL_DETECTION_PHASE: DetectionPhase = {status: DetectionPhase.PENDING}

function haveSameItems(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false
  const setB = new Set(b)
  return a.every(id => setB.has(id))
}

/**
 * Given current state and fetch data, determine the next DetectionPhase (or null if no transition needed).
 * Handles transitions:
 * - PENDING → ACTIVE: when thresholds are met and we have fetch data
 * - ACTIVE → ACTIVE: when we have new fetch data (updates items/lastQueriedLength)
 * Note: ACTIVE → PENDING (content clearing) is handled separately in the hook.
 */
export function computeDetectionPhase(
  detectionPhase: DetectionPhase,
  resolvedData: PotentialDuplicateIssue[],
  currentCombinedLength: number,
  titleMeetsThreshold: boolean,
  bodyMeetsThreshold: boolean,
): DetectionPhase | null {
  if (detectionPhase.status === DetectionPhase.PENDING) {
    if (!(titleMeetsThreshold && bodyMeetsThreshold)) return null
    // Initial state - transition to ACTIVE once threshold is met
    return {
      status: DetectionPhase.ACTIVE,
      lockedItems: resolvedData,
      lastQueriedLength: currentCombinedLength,
    }
  }

  if (detectionPhase.status === DetectionPhase.ACTIVE) {
    const currentNodeIds = resolvedData.map(item => item.nodeId)
    const lockedNodeIds = detectionPhase.lockedItems.map(item => item.nodeId)
    if (haveSameItems(currentNodeIds, lockedNodeIds)) {
      const itemsByNodeId = new Map(resolvedData.map(item => [item.nodeId, item]))
      return {
        ...detectionPhase,
        lockedItems: detectionPhase.lockedItems.map(item => itemsByNodeId.get(item.nodeId) ?? item),
        lastQueriedLength: currentCombinedLength,
      }
    }
    return {
      status: DetectionPhase.ACTIVE,
      lockedItems: resolvedData,
      lastQueriedLength: currentCombinedLength,
    }
  }

  return null
}

/** Holds analytics state per issue creation session. Resets on new session. */
type AnalyticsState = {
  wasIssueClicked: boolean
  issueClickCount: number
  hadDuplicateMatches: boolean
}

const initialAnalyticsState: AnalyticsState = {
  wasIssueClicked: false,
  issueClickCount: 0,
  hadDuplicateMatches: false,
}

// A duplicate row keys its database id as `id` and the global node id as `nodeId`; operations name
// the global id.
const DUPLICATE_IDENTITY_PATH = ['nodeId']

// Stable empty array to prevent infinite re-render loops when items are empty
const EMPTY_ITEMS: PotentialDuplicateIssue[] = []

/**
 * Returns template defaults to exclude from initial threshold calculation.
 */
function getTemplateDefaults(
  template: IssueCreatePayload | undefined,
  issueFormRef: React.RefObject<IssueFormRef | null> | undefined,
): {title: string; body: string} {
  if (!template) return {title: '', body: ''}
  if (instanceOfIssueFormData(template.data)) {
    return {
      title: (template.data.title ?? '').trim(),
      body: issueFormRef?.current?.initialMarkdown() ?? '',
    }
  }
  if (instanceOfIssueTemplateData(template.data)) {
    return {
      title: (template.data.title ?? '').trim(),
      body: template.data.body ?? '',
    }
  }
  return {title: '', body: ''}
}

export function useDuplicateDetection({
  repoOwner,
  repoName,
  title,
  body,
  template,
  issueFormRef,
  isDuplicateDetectionEnabled,
  context,
}: UseDuplicateDetectionOptions): DuplicateDetectionState {
  const issuesNewReactDataRouterEnabled = isFeatureEnabled('issues_new_react_data_router')
  const previousCountRef = useRef<number | null>(null)

  const [queryTitle, setQueryTitle] = useState(title.trim())
  const [queryBody, setQueryBody] = useState(body.trim())

  // Session ID ref for correlating analytics events.
  // Only called from effects/handlers, never during render (SSR-safe in current usage).
  const sessionIdRef = useRef<string | undefined>(undefined)
  const getSessionId = useCallback(() => (sessionIdRef.current ??= crypto.randomUUID()), [])

  // Counter to reset components on new session — SSR-safe (starts at 0 on both server and client).
  const [sessionResetKey, setSessionResetKey] = useState(0)
  const [detectionPhase, setDetectionPhase] = useState<DetectionPhase>(INITIAL_DETECTION_PHASE)
  const [dismissedGroupKeys, setDismissedGroupKeys] = useState<Set<string>>(() => new Set())

  const analyticsRef = useRef<AnalyticsState>({...initialAnalyticsState})

  const debounceQueryTitleUpdate = useDebounce((value: string) => setQueryTitle(value.trim()), DEFAULT_DEBOUNCE_MS)
  const debounceQueryBodyUpdate = useDebounce((value: string) => setQueryBody(value.trim()), DEFAULT_DEBOUNCE_MS)

  useEffect(() => {
    if (!isDuplicateDetectionEnabled) return
    debounceQueryTitleUpdate(title.trim())
  }, [isDuplicateDetectionEnabled, title, debounceQueryTitleUpdate])

  useEffect(() => {
    if (!isDuplicateDetectionEnabled) return
    debounceQueryBodyUpdate(body.trim())
  }, [isDuplicateDetectionEnabled, body, debounceQueryBodyUpdate])

  const {title: templateTitle, body: templateBody} = getTemplateDefaults(template, issueFormRef)

  const normalizedQueryBody = useMemo(() => normalizeForDuplicateThreshold(queryBody), [queryBody])
  // Reduce premature triggering for Issue form and Issue template scenarios.
  const addedThresholdLength = useMemo(() => normalizeForDuplicateThreshold(templateBody).length, [templateBody])

  const {sendRequestComplete, sendIssueClicked, sendIssueCreated, sendDismissed} = useDuplicateDetectionAnalytics({
    context,
    repoNWO: `${repoOwner}/${repoName}`,
    issueKind: template?.kind,
    initialTemplateLength: templateBody.length,
    addedThresholdLength,
  })

  // Ensure that thresholds don't run check against default title and body content.
  const titleMeetsThreshold =
    (templateTitle === '' || queryTitle.trim() !== templateTitle) &&
    queryTitle.trim().length >= DEFAULT_MIN_TITLE_LENGTH
  const bodyMeetsThreshold = normalizedQueryBody.length >= DEFAULT_MIN_BODY_LENGTH + addedThresholdLength
  const currentCombinedLength = queryTitle.trim().length + normalizedQueryBody.length

  // Determine if we should check for duplicates based on current state
  const shouldFetch = useMemo(() => {
    switch (detectionPhase.status) {
      case DetectionPhase.PENDING:
        return titleMeetsThreshold && bodyMeetsThreshold
      case DetectionPhase.ACTIVE:
        return (
          titleMeetsThreshold &&
          bodyMeetsThreshold &&
          meetsDeltaForRecheck(currentCombinedLength, detectionPhase.lastQueriedLength)
        )
    }
  }, [detectionPhase, currentCombinedLength, titleMeetsThreshold, bodyMeetsThreshold])

  const requestedQueryKey = useMemo(
    () =>
      [
        'duplicate-detection',
        repoOwner,
        repoName,
        queryTitle,
        shouldFetch ? queryBody : '', // Avoids caching large body strings when not actually fetching.
        sessionResetKey,
        context,
        addedThresholdLength,
      ] as const,
    [repoOwner, repoName, queryTitle, shouldFetch, queryBody, sessionResetKey, context, addedThresholdLength],
  )
  const [lastFetchedQueryKey, setLastFetchedQueryKey] = useState(requestedQueryKey)
  const queryKey =
    detectionPhase.status === DetectionPhase.ACTIVE && !shouldFetch ? lastFetchedQueryKey : requestedQueryKey
  const {data, isFetching, isError, isPlaceholderData} = useQuery({
    queryKey,
    queryFn: async ({signal, queryKey: fetchedQueryKey}) => {
      setLastFetchedQueryKey(fetchedQueryKey)
      const [, fetchedRepoOwner, fetchedRepoName, fetchedTitle, fetchedBody, , fetchedContext] = fetchedQueryKey

      const response = await verifiedFetchJSON(`/${fetchedRepoOwner}/${fetchedRepoName}/issues/semantically_similar`, {
        method: 'POST',
        body: {
          search: {
            title: fetchedTitle,
            body: fetchedBody,
            page: 1,
            per_page: 3,
            context: fetchedContext,
          },
        },
        signal,
      })

      if (!response.ok) {
        throw new Error(`Failed to fetch similar issues: ${response.status}`)
      }

      const json = await response.json()
      // Response shape: { similar_issues: [{ issue: { id, node_id, number, title, state, ... }, score: number | null }] }
      const potentialDuplicates = json.similar_issues ?? []
      const results = potentialDuplicates.map(
        (item: {
          issue: {
            id: number
            node_id: string
            number: number
            title: string
            state: string
            state_reason: string | null
          }
          score: number | null
        }) => ({
          id: String(item.issue.id),
          nodeId: item.issue.node_id,
          number: item.issue.number,
          title: item.issue.title,
          state: item.issue.state.toUpperCase(),
          stateReason: item.issue.state_reason?.toUpperCase(),
          score: item.score,
        }),
      ) as PotentialDuplicateIssue[]

      sendRequestComplete(getSessionId(), results.length, fetchedTitle.length, fetchedBody.length)
      // Track if we ever got results in this session
      if (results.length > 0) {
        analyticsRef.current.hadDuplicateMatches = true
      }

      return results
    },
    enabled: isDuplicateDetectionEnabled && shouldFetch,
    staleTime: 60 * 1000, // 1 minute,
    placeholderData: keepPreviousData,
  })

  // When `data` changes, we need detectionPhase to be up-to-date *before* the render
  // paints, so `items` reflects the correct state. By computing the state transition
  // during render (not in useEffect), React batches both updates into a single paint.
  // See: https://react.dev/learn/you-might-not-need-an-effect#adjusting-some-state-when-a-prop-changes
  const resolvedFetchData = !isFetching && !isPlaceholderData ? data : undefined
  const normalizedRawBody = useMemo(() => normalizeForDuplicateThreshold(body), [body])

  const CLEARED_THRESHOLD = 1 // Handles select-all-replace
  const contentCleared = title.trim().length <= CLEARED_THRESHOLD || normalizedRawBody.length <= CLEARED_THRESHOLD
  const [prevResolvedData, setPrevResolvedData] = useState<PotentialDuplicateIssue[] | undefined>(undefined)

  // Handle fetch data arrival: PENDING → ACTIVE or ACTIVE → ACTIVE (update items)
  if (isDuplicateDetectionEnabled && resolvedFetchData !== prevResolvedData) {
    setPrevResolvedData(resolvedFetchData)
    if (resolvedFetchData !== undefined && !contentCleared) {
      const nextState = computeDetectionPhase(
        detectionPhase,
        resolvedFetchData,
        currentCombinedLength,
        titleMeetsThreshold,
        bodyMeetsThreshold,
      )
      if (nextState) setDetectionPhase(nextState)
    }
  }

  // Handle when title or body is cleared.
  const [prevContentCleared, setPrevContentCleared] = useState(false)
  if (isDuplicateDetectionEnabled && contentCleared !== prevContentCleared) {
    setPrevContentCleared(contentCleared)
    if (contentCleared && detectionPhase.status === DetectionPhase.ACTIVE) {
      setDetectionPhase({status: DetectionPhase.PENDING})
      setPrevResolvedData(undefined)
    }
  }

  // No other consumer on this route mounts one.
  useEntityFieldsBridge({enabled: issuesNewReactDataRouterEnabled && isDuplicateDetectionEnabled})

  const lockedItems = detectionPhase.status === DetectionPhase.ACTIVE ? detectionPhase.lockedItems : EMPTY_ITEMS

  // Applied to `lockedItems` rather than `data` because they are a snapshot taken at the last phase
  // transition: patching the cache alone would never reach them.
  const optimisticEnabled = issuesNewReactDataRouterEnabled && isDuplicateDetectionEnabled
  const duplicateTargets = useMemo(() => [{queryKey}], [queryKey])
  const rawItems = useOptimisticData(lockedItems, {
    identityPath: DUPLICATE_IDENTITY_PATH,
    targets: duplicateTargets,
    enabled: optimisticEnabled,
  })

  const items = useMemo(() => {
    if (dismissedGroupKeys.size === 0 || rawItems.length === 0) return rawItems
    const currentGroupKey = rawItems
      .map(item => item.nodeId)
      .sort()
      .join(',')
    return dismissedGroupKeys.has(currentGroupKey) ? EMPTY_ITEMS : rawItems
  }, [rawItems, dismissedGroupKeys])

  // Update a displayed issue when subscription data arrives
  const updateIssue = useCallback((update: PotentialDuplicateIssueUpdate) => {
    setDetectionPhase(prev => {
      if (prev.status !== DetectionPhase.ACTIVE) return prev
      return {
        ...prev,
        lockedItems: prev.lockedItems.map(item => {
          if (item.nodeId !== update.nodeId) return item
          return {
            ...item,
            ...(update.title !== undefined && {title: update.title}),
            ...(update.state !== undefined && {state: update.state}),
            ...(update.stateReason !== undefined && {stateReason: update.stateReason}),
          }
        }),
      }
    })
  }, [])

  useEffect(() => {
    if (!isDuplicateDetectionEnabled) return
    if (detectionPhase.status === DetectionPhase.PENDING) return

    if (!isFetching && previousCountRef.current !== items.length) {
      previousCountRef.current = items.length
      const count = items.length
      if (count === 0) {
        announce('No potential duplicates')
      } else if (count === 1) {
        announce('1 potential duplicate')
      } else {
        announce(`${count} potential duplicates`)
      }
    }
  }, [detectionPhase.status, isDuplicateDetectionEnabled, items.length, isFetching])

  // Track issue click
  const trackIssueClicked = useCallback(
    (position: number) => {
      analyticsRef.current.wasIssueClicked = true
      analyticsRef.current.issueClickCount += 1
      sendIssueClicked(getSessionId(), position)
    },
    [sendIssueClicked, getSessionId],
  )

  // Track interactions leading up to issue creation
  const trackIssueCreated = useCallback(
    (issueId: string, issueNumber: number, issueTitle: string, issueBody: string) => {
      const analyticsState = analyticsRef.current
      sendIssueCreated(getSessionId(), {
        issueId,
        issueNumber,
        hadDuplicateMatches: analyticsState.hadDuplicateMatches,
        wasIssueClicked: analyticsState.wasIssueClicked,
        issueClickCount: analyticsState.issueClickCount,
        titleLength: issueTitle.length,
        bodyLength: issueBody.length,
        normalizedBodyLength: normalizeForDuplicateThreshold(issueBody).length,
      })
    },
    [sendIssueCreated, getSessionId],
  )

  /** Handles resetting feedback, analytics, and expand state of the section when 'Create more' is enabled. */
  const resetForNewSession = useCallback(() => {
    previousCountRef.current = null

    setQueryTitle('')
    setQueryBody('')
    setDetectionPhase(INITIAL_DETECTION_PHASE)
    setDismissedGroupKeys(new Set())

    analyticsRef.current = {...initialAnalyticsState}
    sessionIdRef.current = crypto.randomUUID()
    setSessionResetKey(prev => prev + 1)
  }, [])

  const dismissCurrentResults = useCallback(() => {
    if (rawItems.length === 0) return
    const key = rawItems
      .map(item => item.nodeId)
      .sort()
      .join(',')
    setDismissedGroupKeys(prev => new Set([...prev, key]))
    sendDismissed(getSessionId(), rawItems.length)
  }, [rawItems, sendDismissed, getSessionId])

  return {
    items,
    isError,
    isDuplicateDetectionEnabled,
    sessionResetKey,
    updateIssue,
    trackIssueClicked,
    resetForNewSession,
    trackIssueCreated,
    dismissCurrentResults,
  }
}
